import DELIVERY_FORM_CONSTANTS from '@pages/AdditionalServices/tabs/DeliveryForm/consts';
import {
  ContainerRouteCalculationParameters,
  CustomCargoRouteCalculationParameters,
  TruckRouteCalculationParameters,
} from '@pages/AdditionalServices/tabs/DeliveryForm/types';
import { getSerializedShareData } from '@pages/AdditionalServices/tabs/DeliveryForm/useDelivery';
import { OrderProduct } from '@services/requests/orderService/orderProductsLoader/interface';
import { RouteResult } from '@services/requests/orderService/types';
import { shortLinkService } from '@services/requests/shortLinkService';

export const getGeneratedShortLink = async (product: OrderProduct) => {
  switch (product.product_type) {
    case 'route':
      return await getDeliveryShortLinkFromProduct(product);
    case 'complex':
    case 'service':
      return await getAdditionalServiceShortLinkFromProduct(product);
  }
};

export const getAdditionalServiceShortLinkFromProduct = async (product: OrderProduct) => {
  const productData = JSON.parse(product.data);
  const serviceData = Array.isArray(productData) ? productData : [productData];
  const formattedServiceData =
    product.product_type === 'service'
      ? serviceData
      : serviceData.map((product) => JSON.parse(product.data));

  return await shortLinkService().GenerateShortLink({
    page: 'additional-services',
    data: JSON.stringify(formattedServiceData),
  });
};

export const getDeliveryShortLinkFromProduct = async (product: OrderProduct) => {
  const productParams: RouteResult = JSON.parse(product.data);

  const truckParameters: TruckRouteCalculationParameters =
    !productParams.cargoParameters.truckParameters ||
    productParams.cargoParameters.truckParameters.length === 0
      ? {
          ...DELIVERY_FORM_CONSTANTS.routeParams.truckParameters,
          endTransportingConditionId: productParams.cargoParameters.endTransportingConditionId,
          startTransportingConditionId: productParams.cargoParameters.startTransportingConditionId,
        }
      : {
          truckParameters: productParams.cargoParameters.truckParameters.map((tr) => ({
            ...tr,
            withPallets: tr.withPallets ?? false,
            palletsLoaded: tr.palletsLoaded ?? [],
          })),
          isCustomConditionsOpen: productParams.cargoParameters.isDangerCargo ?? false,
          isDangerousCargo: productParams.cargoParameters.isDangerCargo ?? false,
          targetCurrencyId: '1',
          endTransportingConditionId: productParams.cargoParameters.endTransportingConditionId,
          startTransportingConditionId: productParams.cargoParameters.startTransportingConditionId,
        };

  const containerParameters: ContainerRouteCalculationParameters =
    !productParams.cargoParameters.containerParameters ||
    productParams.cargoParameters.containerParameters.length === 0
      ? {
          ...DELIVERY_FORM_CONSTANTS.routeParams.containerParameters,
          endTransportingConditionId: productParams.cargoParameters.endTransportingConditionId,
          startTransportingConditionId: productParams.cargoParameters.startTransportingConditionId,
        }
      : {
          containerParameters: productParams.cargoParameters.containerParameters.map((cp) => ({
            ...cp,
            containerType: '1',
            withPallets: cp.withPallets ?? false,
            palletsLoaded: cp.palletsLoaded ?? [],
          })),
          isCustomConditionsOpen: productParams.cargoParameters.isDangerCargo ?? false,
          isDangerousCargo: productParams.cargoParameters.isDangerCargo ?? false,
          targetCurrencyId: '1',
          endTransportingConditionId: productParams.cargoParameters.endTransportingConditionId,
          startTransportingConditionId: productParams.cargoParameters.startTransportingConditionId,
        };

  const customCargoParameters: CustomCargoRouteCalculationParameters =
    !productParams.cargoParameters.customCargoParameters ||
    productParams.cargoParameters.customCargoParameters.length === 0
      ? {
          ...DELIVERY_FORM_CONSTANTS.routeParams.customCargoParameters,
          endTransportingConditionId: productParams.cargoParameters.endTransportingConditionId,
          startTransportingConditionId: productParams.cargoParameters.startTransportingConditionId,
        }
      : {
          customCargoParameters: productParams.cargoParameters.customCargoParameters.map(
            ({ baseParameters, palletParameters, volumeParameters, customCargoType }) => ({
              baseParameters: {
                height: baseParameters.height.toString(),
                length: baseParameters.length.toString(),
                quantity: baseParameters.quantity,
                weight: baseParameters.weight.toString(),
                width: baseParameters.width.toString(),
              },
              palletParameters: {
                normativeHeightOfStacking: palletParameters.normativeHeightOfStacking.toString(),
                quantity: palletParameters.quantity,
                type: palletParameters.type,
                weight: palletParameters.weight,
              },
              volumeParameters: {
                height: volumeParameters?.normativeHeightOfStacking ?? 1,
                volume: volumeParameters.volume.toString(),
                weight: volumeParameters.weight.toString(),
              },
              parametersType: customCargoType,
              parametersCargoCalcType: customCargoType === 'volume' ? 'volume' : 'base',
            })
          ),
          isCustomConditionsOpen: productParams.cargoParameters.isDangerCargo ?? false,
          isDangerousCargo: productParams.cargoParameters.isDangerCargo ?? false,
          targetCurrencyId: '1',
          endTransportingConditionId: productParams.cargoParameters.endTransportingConditionId,
          startTransportingConditionId: productParams.cargoParameters.startTransportingConditionId,
          cargoFingerprint: productParams.cargoParameters.cargoFingerprint ?? '',
          transportTypes: productParams.cargoParameters.transportTypes ?? [],
        };

  return await shortLinkService().GenerateShortLink({
    page: 'delivery',
    data: getSerializedShareData({
      startLocation: productParams.cargoParameters.startLocation,
      endLocation: productParams.cargoParameters.endLocation,
      startExpeditionDate: new Date(),
      startTransportingCondition: productParams.cargoParameters.startTransportingCondition,
      endTransportingCondition: productParams.cargoParameters.endTransportingCondition,
      containerParameters: containerParameters,
      truckParameters: truckParameters,
      customCargoParameters: customCargoParameters,
      isDangerCargo: productParams.cargoParameters.isDangerCargo ?? false,
      cargoType: productParams.cargoParameters.cargoType,
      requiredTransportingTypes: productParams.cargoParameters.requiredTransportingTypes,
      isNeedContainerRent: productParams.cargoParameters.isNeedContainerRent,
      isNeedPrekeridge: productParams.cargoParameters.isNeedPrekeridge,
      isWithoutOnkeridge: productParams.cargoParameters.isWithoutOnkeridge,
      isWithoutPrekeridge: productParams.cargoParameters.isWithoutPrekeridge,
      isFromTerminalRadius: productParams.cargoParameters.isFromTerminalRadius,
      isToTerminalRadius: productParams.cargoParameters.isToTerminalRadius,
      isWithCrossDocking: productParams.cargoParameters.isWithCrossDocking,
    }),
  });
};
